import * as B from '@blueprintjs/core';
import React from 'react';

import {CartItemV1, isPartialAoiItem} from 'app/modules/Cart';

import cs from './styles.module.scss';

export type SceneStatusId = 'processing' | 'partiallyOrdered' | 'ordered' | 'inCart';

export interface SceneStatus {
  id: SceneStatusId;
  icon: B.IconName;
  /** Spelled out over the thumbnail on hover. */
  label: string;
  /** What the badge alone can't say, for its tooltip. */
  title: string;
}

export interface SceneStatusInput {
  /** Optional for surfaces that carry order state some other way, such as by grouping. */
  isOrdered?: boolean;
  isProcessed?: boolean;
  isPartial?: boolean;
  /** Undefined when the scene isn't in the cart, or the cart is off for this org. */
  cartItem: CartItemV1 | undefined;
}

/**
 * One state per row, cart first. A scene can be both partially ordered and in the cart — ordering
 * a scene in pieces is rare enough that saying both isn't worth the row showing two of everything,
 * and of the two, the cart is the one the reader can still act on.
 */
export const sceneStatus = ({
  isOrdered = false,
  isProcessed = false,
  isPartial = false,
  cartItem,
}: SceneStatusInput): SceneStatus | null => {
  if (cartItem) {
    return {
      id: 'inCart',
      icon: 'shopping-cart',
      label: 'In cart',
      title: isPartialAoiItem(cartItem)
        ? 'A partial area of this scene is in your cart'
        : 'This scene is in your cart',
    };
  }

  if (isOrdered && !isProcessed) {
    return {
      id: 'processing',
      icon: 'time',
      label: 'Processing',
      title: 'This order is still being processed',
    };
  }

  if (isOrdered && isPartial) {
    return {
      id: 'partiallyOrdered',
      icon: 'tick',
      label: 'Partially ordered',
      title: 'Part of this scene has been ordered',
    };
  }

  if (isOrdered) {
    return {
      id: 'ordered',
      icon: 'tick',
      label: 'Ordered',
      title: 'This scene has been ordered',
    };
  }

  return null;
};

/**
 * Drawn over the thumbnail's outside corner, so state costs the row no width. Icon-only: the words
 * are in the overlay below, which the row shows on hover.
 */
export const SceneStatusBadge: React.FunctionComponent<{status: SceneStatus}> = ({status}) => (
  <span
    className={`${cs.badge} ${cs[status.id]}`}
    // Not a B.Tooltip: this sits inside the thumbnail's hover-preview popover target, so a second
    // Blueprint overlay would open on top of the enlarged image.
    title={status.title}
  >
    <B.Icon icon={status.icon} size={11} />
  </span>
);

/**
 * The spelled-out state, written across the thumbnail. It costs the row no width, which is what
 * the date line couldn't give it: at the sidebar's real width a pill beside the date pushes the
 * source name out. The card reveals this on row hover, alongside the row's other hover controls.
 *
 * White on a scrim rather than the state's own color, since the words land on imagery of unknown
 * brightness — the color stays on the resting badge, which is small enough to carry a ring.
 */
export const SceneStatusOverlay: React.FunctionComponent<{status: SceneStatus}> = ({status}) => (
  <span className={cs.overlay}>{status.label}</span>
);
